/**
 * Validates and determines the type of input (link or email)
 * @param {string} input - The input string to validate
 * @returns {object} - Object containing isValid, type, and formatted URL
 */
export const validateAndDetermineType = (input) => {
  if (!input || typeof input !== "string") {
    return { isValid: false, type: null, url: null };
  }

  const trimmedInput = input.trim();

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // URL validation - more strict but handles localhost, IPs, and ports
  const urlRegex =
    /^(https?:\/\/)?([\da-z.-]+|localhost|\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})(:\d+)?(\/[\w.-]*)*\/?$/i;

  // Check if it's an email
  if (emailRegex.test(trimmedInput)) {
    return {
      isValid: true,
      type: "email",
      url: `mailto:${trimmedInput}`,
    };
  }

  // Check if it's a URL - stricter validation
  let isValidUrl = false;

  // Method 1: Try the regex pattern (no spaces allowed)
  if (urlRegex.test(trimmedInput) && !/\s/.test(trimmedInput)) {
    isValidUrl = true;
  }

  // Method 2: If it starts with http/https, validate more strictly
  if (
    !isValidUrl &&
    /^https?:\/\//.test(trimmedInput) &&
    !/\s/.test(trimmedInput)
  ) {
    // Must have at least a domain after protocol
    const afterProtocol = trimmedInput.replace(/^https?:\/\//, "");
    if (
      afterProtocol &&
      afterProtocol.length > 0 &&
      !afterProtocol.startsWith("/")
    ) {
      // Try URL constructor for final validation
      try {
        new URL(trimmedInput);
        isValidUrl = true;
      } catch {
        // Invalid URL
      }
    }
  }

  // Method 3: For URLs without protocol, be more strict
  if (!isValidUrl && !trimmedInput.includes(" ")) {
    try {
      const testUrl = `https://${trimmedInput}`;
      const urlObj = new URL(testUrl);
      // Must have a valid hostname (not just protocol)
      if (urlObj.hostname && urlObj.hostname.length > 0) {
        isValidUrl = true;
      }
    } catch {
      // Not a valid URL
    }
  }

  if (isValidUrl) {
    // Add protocol if missing
    const formattedUrl = trimmedInput.startsWith("http")
      ? trimmedInput
      : `https://${trimmedInput}`;

    return {
      isValid: true,
      type: "link",
      url: formattedUrl,
    };
  }

  return { isValid: false, type: null, url: null };
};
